import { compose, map, range } from 'lodash/fp';
import { Component } from 'react';
import { IconName } from '@optimus-web/icons';
import OverflowedIndicator from './OverflowedIndicator';
import PageButton from './PageButton';
import { GhostIconButton, MenuWrapper } from './styles';
import { PaginationProps } from './types';

const overflowLimit = 2;
const maxButtonCount = 9;

class Pagination extends Component<PaginationProps> {
    mapButtonRange = compose(
        map((i) => (
            <li key={i}>
                <PageButton
                    data-test-button-page={i}
                    value={i}
                    onClick={this.props.onClick}
                    isSelected={this.props.selected === i}
                >
                    {`${i + 1}`}
                </PageButton>
            </li>
        )),
        range
    );

    getButtonRange = (hasPreviousOverflow: boolean, hasNextOverflow: boolean): { low: number; high: number } => {
        const { selected, size } = this.props;

        if (hasPreviousOverflow && hasNextOverflow) {
            return { low: selected - overflowLimit, high: selected + overflowLimit + 1 };
        }

        const visibleButtons = maxButtonCount - overflowLimit;
        if (hasPreviousOverflow) {
            return { low: size - visibleButtons, high: size };
        }

        if (hasNextOverflow) {
            return { low: 0, high: visibleButtons };
        }

        return { low: 0, high: size };
    };

    handleFirstPageClick = (position: 'first' | 'last') => {
        const { onClick, size } = this.props;
        const positionNumber = position === 'first' ? 0 : size - 1;

        onClick(positionNumber);
    };

    handlePrevPageClick = () => {
        const { onClick, selected } = this.props;
        onClick(selected - 1);
    };

    handleNextPageClick = () => {
        const { onClick, selected } = this.props;
        onClick(selected + 1);
    };

    render() {
        const { selected, size, onClick, ...other } = this.props;
        if (size < 1) {
            return null;
        }

        const maxIndex = size - 1;

        const hasPrevious = selected > 0;
        const hasNext = selected < maxIndex;

        const hasOverflow = size > maxButtonCount;
        const hasPreviousOverflow = hasOverflow && selected - overflowLimit > overflowLimit;
        const hasNextOverflow = hasOverflow && selected + overflowLimit < maxIndex - overflowLimit;

        const { low, high } = this.getButtonRange(hasPreviousOverflow, hasNextOverflow);

        return (
            <MenuWrapper itemAlignment="center" {...other} as="menu">
                <li>
                    <GhostIconButton
                        variant="ghost"
                        icon={IconName.FastBackwardSimple}
                        onClick={() => this.handleFirstPageClick('first')}
                        disabled={!hasPrevious}
                        size="small"
                        data-test-page-first
                    />
                </li>
                <li>
                    <GhostIconButton
                        variant="ghost"
                        icon={IconName.ChevronLeft}
                        onClick={this.handlePrevPageClick}
                        disabled={!hasPrevious}
                        size="small"
                        data-test-page-previous
                    />
                </li>
                {hasPreviousOverflow ? (
                    <>
                        <li>
                            <PageButton data-test-button-page={0} value={0} onClick={onClick}>{`${1}`}</PageButton>
                        </li>
                        <li>
                            <OverflowedIndicator />
                        </li>
                    </>
                ) : null}
                {this.mapButtonRange(low, high)}
                {hasNextOverflow ? (
                    <>
                        <li>
                            <OverflowedIndicator />
                        </li>
                        <li>
                            <PageButton
                                data-test-button-page={maxIndex}
                                value={maxIndex}
                                onClick={onClick}
                            >{`${size}`}</PageButton>
                        </li>
                    </>
                ) : null}
                <li>
                    <GhostIconButton
                        variant="ghost"
                        icon={IconName.ChevronRight}
                        onClick={this.handleNextPageClick}
                        disabled={!hasNext}
                        size="small"
                        data-test-page-next
                    />
                </li>
                <li>
                    <GhostIconButton
                        variant="ghost"
                        icon={IconName.FastForwardSimple}
                        onClick={() => this.handleFirstPageClick('last')}
                        disabled={!hasNext}
                        size="small"
                        data-test-page-last
                    />
                </li>
            </MenuWrapper>
        );
    }
}

export default Pagination;
